import { caaCustomerQuery } from "@repo/shopifyV2/graphql/customer-account-gql";
import type { ICaaCustomer, ICaaCustomerQuery } from "@repo/shopifyV2/interface/customer-account";

import { caaRequest } from "@lib/caaClient";
import { HIKO_LOGIN_NOTICE_KEY, HIKO_LOGIN_PROVIDER_KEY, getHikoSessionToken, hikoLogout, toCaaToken } from "@lib/hikoHeadlessSession";

import { useCustomerStore } from "@store/customer";

export type THikoLoginResult = { status: "ok"; customer: ICaaCustomer } | { status: "blocked" } | { status: "error" };

/**
 * Hiko 위젯에서 받은 access_token을 store에 반영하기 전에 계정 상태(탈퇴요청 등)를 검증한다.
 * signin-headless의 `hiko:login` 이벤트 경로(SocialLoginCaa)와 앱 부팅 시 세션 동기화 경로(App.tsx)
 * 둘 다 이 함수를 거치도록 해서 검증 로직이 한쪽에서만 빠지는 걸 방지한다.
 *
 * "error"/"blocked" 상태에선 로컬 store 로그아웃뿐 아니라 브로커 로그아웃(hikoLogout)도 여기서
 * 바로 처리한다 — 미뤄두면(예: 모달 확인 시점까지) 브로커 세션이 살아있는 채로 남아 다음 로그인
 * 시도(다른 provider 포함)가 이 세션과 꼬일 수 있다.
 *
 * signin-headless 0.2.0부터 hikoLogout()(el.logout())이 브로커 로그아웃 후 Shopify 로그아웃
 * 페이지로 곧바로 리다이렉트한다 — 즉 아래 `return`문까지 정상적으로 도달하지 못하고 페이지가
 * 이동해버릴 수 있다. 그래서 리다이렉트 전에 HIKO_LOGIN_NOTICE_KEY를 sessionStorage에 먼저
 * 남겨두고, 왕복 후 돌아온 페이지(App.tsx 부팅 시점)에서 그 값을 읽어 모달/토스트를 띄운다 —
 * 이 함수의 반환값(THikoLoginResult)에만 의존하면 안 됨.
 */
export async function acceptHikoLogin(token: { accessToken: string; expiresAt: number }): Promise<THikoLoginResult> {
  useCustomerStore.getState().loginCaa(toCaaToken(token));

  // caaRequest 는 매 호출마다 네트워크로 나가므로 별도 no-cache 설정이 필요 없다
  const data = await caaRequest<ICaaCustomerQuery>(caaCustomerQuery);

  if (!data?.customer) {
    useCustomerStore.getState().logout();
    sessionStorage.setItem(HIKO_LOGIN_NOTICE_KEY, "error");
    try {
      await hikoLogout();
    } catch {
      // 리다이렉트 전 네트워크 실패 등 — 로컬 로그아웃은 이미 처리됨
    }
    return { status: "error" };
  }

  if (data.customer.tags.includes("탈퇴요청")) {
    useCustomerStore.getState().logout();
    sessionStorage.setItem(HIKO_LOGIN_NOTICE_KEY, "blocked");
    try {
      await hikoLogout();
    } catch {
      // 리다이렉트 전 네트워크 실패 등 — 로컬 로그아웃은 이미 처리됨
    }
    return { status: "blocked" };
  }

  useCustomerStore.getState().updateCaa(data.customer);

  const provider = sessionStorage.getItem(HIKO_LOGIN_PROVIDER_KEY);
  if (provider) {
    useCustomerStore.getState().setCaaLoginProvider(provider);
    sessionStorage.removeItem(HIKO_LOGIN_PROVIDER_KEY);
  }

  return { status: "ok", customer: data.customer };
}

/**
 * 앱 부팅 시 1회 호출. 소셜 로그인은 Shopify 호스티드 페이지를 거치는 풀 리다이렉트라, 로그인
 * 드로어(SocialLoginCaa)가 리다이렉트-백 시점엔 이미 언마운트되어 있어 `hiko:login` 이벤트를
 * 아무도 못 받을 수 있음(실제로 이 경로가 로그인이 반영되는 유일한 경로인 경우가 많음). 이 경우를
 * 대비해 위젯의 브로커 세션을 직접 확인해 동기화한다. 이미 유효한 caaToken이 있으면(네이티브
 * 로그인 등) 건드리지 않는다.
 *
 * 결과(THikoLoginResult)를 그대로 반환 — 모달/토스트 등 UI는 화면 컨텍스트가 있는 호출부(App.tsx)가
 * 처리한다.
 */
export async function syncHikoSessionOnBoot(): Promise<THikoLoginResult | null> {
  const { caaToken } = useCustomerStore.getState();
  if (caaToken?.accessToken && new Date(caaToken.expiresAt).getTime() > Date.now()) return null;

  const token = await getHikoSessionToken();
  if (!token) return null;

  return acceptHikoLogin(token);
}
